import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Button, Icon } from '@iq/react-components';

import { useDebounce, useClientSize, useMemoizedDeepComparison } from '../../../utils';

import FilePreviewModal from '../../FilePreviewModal';
import FileUploadModal from './components/FileUploadModal';
import FileEditModal from './components/FileEditModal';
import FiletypeIcon from '../../FiletypeIcon';
import ConfirmationDialog from '../../ConfirmationDialog';
import FilesHeader from './components/FilesHeader';
import Loader from '../../Loader';
import Pagination from '../../Pagination';
import RenderFiles from './components/RenderFiles';

import {
  getComponentsUpdatedAt,
  getScopedComponents,
  getStaticComponents,
  getFilteredActiveComponentId,
} from '../../../bundles/components';
import { requestTypes, getTypes } from '../../../bundles/types';
import { requestTags, getTags } from '../../../bundles/tags';
import { getHasPermission } from '../../../bundles/auth';
import {
  useFilesSession,
  getFiles,
  requestFiles,
  deleteFile,
  downloadFiles,
} from '../../../bundles/files';

const empty = [];
const emptyObject = {};

const filterDeletedFiles = (fileList, idsToDelete) =>
  idsToDelete.filter((fileId) => fileList.map((f) => f.id).includes(fileId));

const FilesPanel = React.memo(
  ({
    componentScope = { selectedScope: 'all' },
    format = 'widest',
    layouts = 'Grid & list',
    hideToolbar = false,
    fileLimit = 20,
    filters: {
      includeFiles: { active: incFilesActive, files: includeFiles = empty } = emptyObject,
      includeTypes: { active: incTypesActive, types: includeTypes = empty } = emptyObject,
      excludeTypes: { active: excTypesActive, types: excludeTypes = empty } = emptyObject,
      includeTags: {
        active: incTagsActive,
        operator: incTagsOperator = 'or',
        tags: includeTags = empty,
      } = emptyObject,
      excludeTags: {
        active: excTagsActive,
        operator: excTagsOperator = 'or',
        tags: excludeTags = empty,
      } = emptyObject,
    } = emptyObject,
    hideSearch = false,
    hideFilters = false,
    hideUploadButton = false,
    isFullScreen = false,
    disableSelect = false,
    showFileExtension = true,
    selector = false,
    onAttach = () => null,
    onClose = () => null,
    site,
    filesToList = [],
    onFileDelete = () => null,
    isEventPage = false,
    editEvent = false,
  }) => {
    const dispatch = useDispatch();
    const { org, id: siteId } = site;
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [{ width }, clientReference] = useClientSize();

    const onKeyDown = (event) => {
      const { altKey: a, ctrlKey: c, metaKey: m, shiftKey: s } = event;
      if (s && !(a || c || m)) {
        setIsShiftPressed(true);
      } else {
        setIsShiftPressed(false);
      }
    };

    const onKeyUp = (event) => {
      if (event.keyCode === 16) {
        setIsShiftPressed(false);
      }
    };

    const canWriteFiles = useSelector((state) =>
      getHasPermission(state, 'files/Write', { org, site: siteId })
    );
    const canDeleteFiles = useSelector((state) =>
      getHasPermission(state, 'files/Delete', { org, site: siteId })
    );
    const canEdit = !selector && canWriteFiles;
    const canDelete = !selector && canDeleteFiles;
    const canDownload = !selector;

    useEffect(() => {
      dispatch(requestTypes(siteId));
      dispatch(requestTags(siteId));
      document.addEventListener('keydown', onKeyDown);
      document.addEventListener('keyup', onKeyUp);
      return () => {
        document.removeEventListener('keydown', onKeyDown);
        document.removeEventListener('keyup', onKeyUp);
      };
    }, [dispatch, siteId]);

    const allTypes = useSelector(getTypes);
    const allTags = useSelector(getTags);

    const availableTags = useMemo(() => {
      if (incTagsActive && includeTags.length > 0 && incTagsOperator === 'or') {
        return allTags.filter(({ id }) => includeTags.includes(id));
      }
      if (excTagsActive && excludeTags.length > 0 && excTagsOperator === 'or') {
        return allTags.filter(({ id }) => !excludeTags.includes(id));
      }
      return allTags;
    }, [
      allTags,
      incTagsActive,
      includeTags,
      excTagsActive,
      excludeTags,
      incTagsOperator,
      excTagsOperator,
    ]);

    const allExcludes = useMemo(() => {
      if (excTagsActive && excludeTags.length > 0) {
        return allTags.filter(({ id }) => excludeTags.includes(id));
      }
      return null;
    }, [allTags, excTagsActive, excludeTags]);

    const allIncludes = useMemo(() => {
      if (incTagsActive && includeTags.length > 0) {
        return allTags.filter(({ id }) => includeTags.includes(id));
      }
      return null;
    }, [allTags, incTagsActive, includeTags]);

    const limitTags = useMemo(
      () => ({
        includesTogether: incTagsActive && incTagsOperator === 'and' ? includeTags : null,
        excludesTogether: excTagsActive && excTagsOperator === 'and' ? excludeTags : null,
        fixedTags: incTagsActive && includeTags.length > 0 && incTagsOperator === 'or',
        allExcludes,
        allIncludes,
      }),
      [
        incTagsActive,
        incTagsOperator,
        includeTags,
        allIncludes,
        excTagsActive,
        excTagsOperator,
        excludeTags,
        allExcludes,
      ]
    );

    const availableTypes = useMemo(() => {
      const types = incTypesActive && includeTypes.length > 0 ? includeTypes : allTypes;

      if (excTypesActive && excludeTypes.length > 0) {
        return types.filter((type) => !excludeTypes.includes(type));
      }
      return types;
    }, [allTypes, incTypesActive, includeTypes, excTypesActive, excludeTypes]);

    const [filesToEdit, setFilesToEdit] = useState(null);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [displayUploadModal, setDisplayUploadModal] = useState(false);
    const [filesToDelete, setFilesToDelete] = useState([]);
    const [filterValue, setFilterValue] = useState('');
    const [fileViewType, setFileViewType] = useState(
      ['Only list', 'List & grid'].includes(layouts) ? 'table' : 'grid'
    );

    const [page, setPage] = useState(1);

    const [previewFileId, setPreviewFileId] = useState(null);
    const [activeBookmark, setActiveBookmark] = useState(null);

    const [typeFilter, setTypeFilter] = useState([]);
    const [tagFilter, setTagFilter] = useState([]);
    const [sortBy, setSortBy] = useState('createdAt');
    const [order, setOrder] = useState('desc');

    const [selectedFiles, setSelectedFiles] = useState([]);
    const selectedFilesIds = useMemo(() => selectedFiles.map((f) => f.id), [selectedFiles]);

    const sessionId = useFilesSession();
    const { files, pages, loading } = useSelector((state) => getFiles(state, sessionId));

    const activeComponentId = useSelector(getFilteredActiveComponentId);
    const components = useSelector(getStaticComponents);
    const memoizedComponents = useMemoizedDeepComparison(components);
    const componentsUpdatedAt = useSelector(getComponentsUpdatedAt);
    const { selectedScope, activeComponentTypeFilter } = componentScope;

    const availableComponents = useMemo(() => {
      if (activeComponentTypeFilter && activeComponentTypeFilter !== 'any') {
        return components.filter((c) => c.type === activeComponentTypeFilter);
      }
      return components;
    }, [components, activeComponentTypeFilter]);

    const memoizedComponentScope = useMemoizedDeepComparison(componentScope);
    const scopedComponents = useMemo(
      () =>
        getScopedComponents({
          components: memoizedComponents,
          componentsUpdatedAt,
          activeComponentId,
          componentScope: memoizedComponentScope,
        }),
      [componentsUpdatedAt, activeComponentId, memoizedComponentScope, memoizedComponents]
    );

    const presetTagFilters = useMemo(() => {
      const filters = [];
      if (incTagsActive && includeTags.length > 0) {
        filters.push({
          filterType: 'include',
          operator: incTagsOperator,
          tags: includeTags,
        });
      }
      if (excTagsActive && excludeTags.length > 0) {
        filters.push({
          filterType: 'exclude',
          operator: excTagsOperator,
          tags: excludeTags,
        });
      }
      return filters;
    }, [incTagsActive, incTagsOperator, includeTags, excTagsActive, excTagsOperator, excludeTags]);

    const query = useMemo(() => {
      const includeIndex = presetTagFilters.findIndex((f) => f.filterType === 'include');
      // if we have tagFilters (user-defined) we know includes are using 'and' operator
      // (tag filter disabled/not shown if includes use 'or')
      // so we replace preset tags with our filters
      let tagFilters = [...presetTagFilters];
      if (includeIndex !== -1 && tagFilter.length > 0) {
        tagFilters[includeIndex] = {
          ...tagFilters[includeIndex],
          tags: tagFilter,
        };
      } else if (includeIndex === -1 && tagFilter.length > 0) {
        tagFilters = [{ filterType: 'include', operator: 'and', tags: tagFilter }, ...tagFilters];
      }
      if (includeIndex !== -1 && !hideToolbar && !hideFilters && tagFilter.length === 0) {
        // delete tagFilters[includeIndex];
        tagFilters.splice(includeIndex, 1);
      }

      const activeIncTypes = incTypesActive ? includeTypes : empty;
      const includeTypesFilter = typeFilter.length > 0 ? typeFilter : activeIncTypes;

      const requestQuery = {
        org,
        site: siteId,
        tags: tagFilters,
        types: includeTypesFilter,
        excludeTypes: excTypesActive ? excludeTypes : empty,
        files: incFilesActive ? includeFiles : empty,
        search: filterValue.length > 0 ? filterValue : null,
        sortBy,
        order,
        page,
        limit: fileLimit,
      };

      if (
        selectedScope !== 'all' ||
        (activeComponentId && activeComponentTypeFilter && activeComponentTypeFilter !== 'any')
      ) {
        if (scopedComponents.length > 0) {
          requestQuery.components = scopedComponents.map((c) => c.id);
        } else {
          // if no components we poison the query so we get no results
          requestQuery.site = '___NO_SITE___';
        }
      }
      return requestQuery;
    }, [
      selectedScope,
      activeComponentTypeFilter,
      scopedComponents,
      tagFilter,
      typeFilter,
      incTypesActive,
      includeTypes,
      incFilesActive,
      includeFiles,
      filterValue,
      excTypesActive,
      excludeTypes,
      presetTagFilters,
      sortBy,
      order,
      page,
      fileLimit,
      activeComponentId,
      hideFilters,
      hideToolbar,
      org,
      siteId,
    ]);

    const onPageChanged = useDebounce(() => {
      dispatch(requestFiles(sessionId, query));
      setSelectedFiles([]);
    }, 300);

    useEffect(() => {
      if (isMounted) {
        onPageChanged();
      } else {
        setIsMounted(true);
      }
    }, [page, isMounted, onPageChanged]);

    const onFilterValueChanged = useDebounce(() => {
      setPage(1);
      dispatch(requestFiles(sessionId, { ...query, page: 1 }));
    }, 300);

    useEffect(() => {
      onFilterValueChanged();
    }, [
      scopedComponents,
      tagFilter,
      typeFilter,
      includeFiles,
      includeTypes,
      filterValue,
      excludeTypes,
      presetTagFilters,
      sortBy,
      order,
      fileLimit,
      onFilterValueChanged,
    ]);

    const filteredBookmarkFiles = useMemo(() => {
      if (files && (incTagsActive || excTagsActive || tagFilter.length > 0)) {
        return files.map((f) => {
          if (f.bookmarks && f.bookmarks.length > 0) {
            const { bookmarks } = f;
            return { ...f, bookmarks };
          }
          return f;
        });
      }
      if (files) return files;
      return null;
    }, [files, incTagsActive, excTagsActive, tagFilter]);

    useEffect(() => {
      if (filesToDelete.length > 0) {
        const filtered = filterDeletedFiles(files, filesToDelete);
        if (filtered.length !== filesToDelete.length) {
          setFilesToDelete(filtered);
        }
      }
    }, [files, filesToDelete]);

    const onSelectFile = useCallback(
      (fileItem) => {
        if (selectedFiles.some((f) => f.id === fileItem.id)) {
          setSelectedFiles(selectedFiles.filter((f) => f.id !== fileItem.id));
        } else {
          setSelectedFiles([...selectedFiles, fileItem]);
        }
      },
      [selectedFiles]
    );

    const toggleSelectAllFiles = useCallback(
      (filesToSelect) => {
        if (selectedFiles.length === filesToSelect.length) {
          setSelectedFiles([]);
        } else {
          setSelectedFiles(filesToSelect);
        }
      },
      [selectedFiles]
    );

    const onDelete = async (id) => {
      setFilesToDelete([id]);
    };

    const onDownload = (fileId) => {
      const fileObject = files.find((f) => f.id === fileId);
      if (fileObject) {
        dispatch(downloadFiles(fileObject));
      }
    };

    const onDownloadSelected = () => {
      if (selectedFiles.length > 1) {
        dispatch(downloadFiles(selectedFiles, 'download.zip'));
      } else if (selectedFiles.length === 1) {
        dispatch(downloadFiles(selectedFiles[0]));
      }
    };

    const onDeleteSelected = () => {
      const fileIds = selectedFiles.filter((f) => !f.bookmarkData).map(({ id }) => id);
      setFilesToDelete(fileIds);
      setSelectedFiles(selectedFiles.filter((f) => !fileIds.includes(f.id)));
    };

    const getConfirmDeletionBody = useCallback(() => {
      const plural = filesToDelete.length > 1;
      const fileObjs = filesToDelete.map((id) => files.find((f) => f.id === id));

      return (
        <>
          <p>{`Are you sure you want to delete the following file${plural ? 's' : ''}?`}</p>
          <div className="filename-container">
            {fileObjs.map((f) => {
              if (!f) {
                return false;
              }
              let contexts = {};
              if (f.context && Array.isArray(f.context)) {
                contexts = f.context.reduce((acc, context) => {
                  if (context.type) {
                    acc[context.type] = (acc[context.type] || 0) + 1;
                  }
                  return acc;
                }, {});
              } else if (f.context && f.context.type) {
                contexts = { [f.context.type]: 1 };
              }
              delete contexts.general;
              return (
                <div
                  key={f.id}
                  className="filename"
                >
                  <FiletypeIcon file={f} />
                  <span>{f.filename}</span>
                  {Object.keys(contexts).length > 0 && (
                    <>
                      {Object.entries(contexts).map(([k, v]) => (
                        <span key={`context-${k}`}>{`(${v} ${k} attachment${
                          v > 1 ? 's' : ''
                        }) `}</span>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      );
      // 'files' is left out as a dep on purpose to prevent
      // file objects from being undefined at point of confirmation
    }, [filesToDelete, files]);

    const onConfirmDeletion = () => {
      for (const id of filesToDelete) dispatch(deleteFile(id));
    };

    const onEdit = (id) => {
      setFilesToEdit(files.filter(({ id: _id }) => _id === id));
    };

    const onEditSelected = () => {
      setFilesToEdit(selectedFiles.filter((f) => !f.bookmarkData));
    };

    const onPreview = useCallback(
      (fileItem, bookmark) => {
        const fileWithAllBookmarks = files.find((f) => f.id === fileItem.id);
        if (!fileWithAllBookmarks) return;

        if (
          activeComponentId &&
          fileItem.components &&
          !fileItem.components.includes(activeComponentId) &&
          !bookmark
        ) {
          bookmark = fileItem.bookmarks.find((b) => b.components.includes(activeComponentId));
        }

        if (bookmark !== undefined) {
          setActiveBookmark(bookmark.id);
        }

        setPreviewFileId(fileWithAllBookmarks.id);
      },
      [files, activeComponentId]
    );

    const onPreviewToggleItem = useCallback(
      (fileId, direction) => {
        const currentPreviewIndex = files.map((c) => c.id).indexOf(fileId);

        let nextItem;
        if (direction === 'next') {
          nextItem =
            currentPreviewIndex === files.length - 1 ? files[0] : files[currentPreviewIndex + 1];
        } else if (direction === 'prev') {
          nextItem = currentPreviewIndex === 0 ? files.at(-1) : files[currentPreviewIndex - 1];
        }

        onPreview(nextItem);
      },
      [files, onPreview]
    );

    const onDrop = (acceptedFiles) => {
      setUploadFiles(acceptedFiles);
      setDisplayUploadModal(true);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      disabled: !canEdit,
      onDrop,
    });

    const onClickFile = (fileItem) => {
      if (isShiftPressed) {
        onSelectFile(fileItem);
      } else {
        onPreview(fileItem);
      }
    };

    const onStepNext = useCallback(
      (fileId) => onPreviewToggleItem(fileId, 'next'),
      [onPreviewToggleItem]
    );
    const onStepPrevious = useCallback(
      (fileId) => onPreviewToggleItem(fileId, 'prev'),
      [onPreviewToggleItem]
    );

    return (
      <>
        {isEventPage ? (
          <>
            {filesToList.map((fileItem) => (
              <div
                key={fileItem.id || fileItem.filename || fileItem.name}
                className={`event-modal__files ${editEvent ? '' : 'editable'}`}
                onClick={editEvent ? null : () => onClickFile(fileItem)}
                tabIndex={editEvent ? undefined : 0}
                role={editEvent ? undefined : 'button'}
                onKeyDown={
                  editEvent
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onClickFile(fileItem);
                        }
                      }
                }
              >
                <FiletypeIcon
                  file={{
                    fileType: fileItem.site ? fileItem.fileType : fileItem.type,
                    filename: fileItem.name || fileItem.filename,
                    mime: fileItem.mime || fileItem.type,
                  }}
                />
                <span>{fileItem.name || fileItem.filename}</span>
                {editEvent && (
                  <Button
                    slim
                    activity="secondary"
                    icon={
                      <Icon
                        icon="he-delete"
                        onClick={() => onFileDelete(fileItem)}
                      />
                    }
                  />
                )}
              </div>
            ))}
          </>
        ) : (
          <div
            ref={clientReference}
            className={`files-panel-component ${loading ? 'updating-files' : 'loaded'}`}
          >
            <FilesHeader
              availableTags={availableTags}
              availableTypes={availableTypes}
              canDelete={canDelete}
              canDownload={canDownload}
              canEdit={canEdit}
              disableSelect={disableSelect}
              fileViewType={fileViewType}
              files={files}
              filterValue={filterValue}
              format={format}
              hideFilters={hideFilters}
              hideSearch={hideSearch}
              hideToolbar={hideToolbar}
              hideUploadButton={hideUploadButton}
              includeTags={(incTagsActive && includeTags) || []}
              includeTypes={(incTypesActive && includeTypes) || []}
              isFullScreen={isFullScreen}
              layouts={layouts}
              limitTags={limitTags}
              onDeleteSelected={onDeleteSelected}
              onDownloadSelected={onDownloadSelected}
              onEditSelected={onEditSelected}
              selectedFiles={selectedFiles}
              setDisplayUploadModal={setDisplayUploadModal}
              setFileViewType={setFileViewType}
              setFiletypeFilter={setTypeFilter}
              setFilterValue={setFilterValue}
              setTagFilter={setTagFilter}
              toggleSelectAllFiles={toggleSelectAllFiles}
              width={width}
            />
            <div className="files-panel">
              <div
                {...getRootProps()}
                className="drop-wrapper"
              >
                {isDragActive && canEdit && (
                  <>
                    <input {...getInputProps()} />
                    <div className="drop-zone">
                      <p>Drop the files here...</p>
                    </div>
                  </>
                )}
                <RenderFiles
                  activeComponentId={activeComponentId}
                  canDelete={canDelete}
                  canDownload={canDownload}
                  canEdit={canEdit}
                  components={components}
                  disableSelect={disableSelect}
                  fileViewType={fileViewType}
                  files={
                    filteredBookmarkFiles === null
                      ? null
                      : filteredBookmarkFiles.map((fileItem) => ({
                          ...fileItem,
                          isSelected: selectedFilesIds.includes(fileItem.id),
                        }))
                  }
                  filterValue={filterValue}
                  format={format}
                  isFullScreen={isFullScreen}
                  isShiftPressed={isShiftPressed}
                  onDelete={onDelete}
                  onDownload={onDownload}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onSelectFile={onSelectFile}
                  selectedFiles={selectedFiles}
                  showFileExtension={showFileExtension}
                  tagFilter={tagFilter}
                  setSortBy={setSortBy}
                  sortBy={sortBy}
                  setOrder={setOrder}
                  order={order}
                />
              </div>

              <Pagination
                pages={pages}
                page={page}
                setPage={setPage}
              />

              {selector && (
                <div className="files-panel-component__selector-actions">
                  <Button
                    onClick={() => onAttach(selectedFiles)}
                    disabled={selectedFiles.length === 0}
                  >
                    {`Attach file${selectedFiles.length > 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    activity="secondary"
                    type="button"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                </div>
              )}

              {loading && (
                <div className="overlay-loader">
                  <Loader />
                </div>
              )}

              {displayUploadModal && (
                <FileUploadModal
                  files={uploadFiles}
                  activeComponentId={activeComponentId}
                  availableComponents={availableComponents}
                  availableTags={availableTags}
                  availableTypes={availableTypes}
                  limitTags={limitTags}
                  typeFilter={typeFilter}
                  includeTags={includeTags}
                  onCloseModal={() => {
                    setUploadFiles([]);
                    setDisplayUploadModal(false);
                  }}
                />
              )}

              {filesToDelete.length > 0 && (
                <ConfirmationDialog
                  onCancel={() => setFilesToDelete([])}
                  onConfirm={onConfirmDeletion}
                  confirmType="danger"
                  title="Remove Files"
                  body={getConfirmDeletionBody()}
                  confirmText="Remove"
                />
              )}

              {filesToEdit && (
                <FileEditModal
                  files={filesToEdit}
                  availableComponents={availableComponents}
                  availableTags={availableTags}
                  availableTypes={availableTypes}
                  limitTags={limitTags}
                  onCloseModal={() => {
                    setSelectedFiles([]);
                    setFilesToEdit(false);
                  }}
                />
              )}
            </div>
          </div>
        )}
        {previewFileId && (
          <FilePreviewModal
            fileId={previewFileId}
            files={files}
            activeBookmark={activeBookmark}
            setActiveBookmark={setActiveBookmark}
            onCloseModal={() => {
              setPreviewFileId(null);
              setActiveBookmark(null);
            }}
            onStepNext={onStepNext}
            onStepPrevious={onStepPrevious}
            onPreview={onPreview}
            canEdit={canEdit}
          />
        )}
      </>
    );
  },
  (previousProperties, nextProperties) =>
    JSON.stringify(previousProperties) === JSON.stringify(nextProperties)
);

export default FilesPanel;
